import { HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { AuthenticatedRestService, ParamsObject, Patient, RestResponse } from '@mmx/shared'
import moment from 'moment'
import { BehaviorSubject, catchError, filter, finalize, map, Observable, of, ReplaySubject, shareReplay, skip, startWith, switchMap, tap } from 'rxjs'

export interface StructuredSearchFields {
  name: string
  eid?: string
  phones: string
  dob: moment.Moment
}

export enum SearchDocumentType {
  PATIENTS = 'patients',
  APPOINTMENTS = 'appts',
  INVOICES = 'invoices',
  ESTIMATE_BALANCES = 'estimate-balances',
}

function createPatientObjects(object) {
  const { results, current, size, total_pages, total_results } = object.data.patients
  const data = results.map((p) => new Patient(p))

  return {
    data,
    page: {
      current: current - 1,
      size,
      totalPages: total_pages,
      totalResults: total_results,
    },
  }
}

@Injectable({
  providedIn: 'root',
})
export class SearchService extends AuthenticatedRestService {
  private route = '/clinicians/search'

  searchPatientsSubject = new ReplaySubject<ParamsObject>(1)
  loading$ = new BehaviorSubject<boolean>(false)
  private readonly _unavailable$ = new BehaviorSubject<boolean>(false)
  /** Whether the last search finished since the subscription failed, so a screen never shows the failure an earlier one left. */
  readonly unavailable$ = this._unavailable$.pipe(skip(1), startWith(false))

  private request = (params: HttpParams) => this.get<any>(this.route, params)

  searchResult$ = this.searchPatientsSubject.pipe(
    switchMap((params) => {
      this.loading$.next(true)

      return this.searchPatients(params).pipe(
        map((response) => response.data.patients.unavailable ? null : createPatientObjects(response)),
        catchError(() => of(null)),
        finalize(() => this.loading$.next(false)),
      )
    }),
    tap((result) => this._unavailable$.next(!result)),
    filter(Boolean),
    shareReplay(1),
  )

  searchPatients(search: ParamsObject): Observable<RestResponse<any>> {
    return this.request(this.convertToParams(search))
  }
}
